// Define event map interface for type safety
interface EventMap {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any[]
}

// Default event map if none provided
interface DefaultEventMap extends EventMap {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any[]
}

export class EventEmitterTyped<TEventMap extends EventMap = DefaultEventMap> {
  private listeners: {
    [K in keyof TEventMap]?: Array<(...args: TEventMap[K]) => void>
  } = {}

  addListener<K extends keyof TEventMap>(
    event: K,
    callback: (...args: TEventMap[K]) => void
  ): this {
    if (!this.listeners[event]) {
      this.listeners[event] = []
    }
    this.listeners[event]!.push(callback)
    return this
  }

  removeListener<K extends keyof TEventMap>(
    event: K,
    callback: (...args: TEventMap[K]) => void
  ): this {
    if (!this.listeners[event]) {
      return this
    }

    const collection = this.listeners[event]!
    const index = collection.indexOf(callback)
    if (index !== -1) {
      collection.splice(index, 1)
    }
    return this
  }

  // Aliases
  on<K extends keyof TEventMap>(
    event: K,
    callback: (...args: TEventMap[K]) => void
  ): this {
    return this.addListener(event, callback)
  }

  off<K extends keyof TEventMap>(
    event: K,
    callback: (...args: TEventMap[K]) => void
  ): this {
    return this.removeListener(event, callback)
  }

  emit<K extends keyof TEventMap>(event: K, ...args: TEventMap[K]): void {
    if (!this.listeners[event]) {
      return
    }

    const collection = this.listeners[event]!
    for (const callback of collection) {
      callback(...args)
    }
  }

  // Get all listeners for an event
  getListeners<K extends keyof TEventMap>(
    event: K
  ): Array<(...args: TEventMap[K]) => void> {
    return this.listeners[event] ? [...this.listeners[event]!] : []
  }

  // Remove all listeners for an event, or all listeners if no event specified
  removeAllListeners<K extends keyof TEventMap>(event?: K): this {
    if (event) {
      delete this.listeners[event]
    } else {
      this.listeners = {}
    }
    return this
  }

  // Get list of events that have listeners
  eventNames(): Array<keyof TEventMap> {
    return Object.keys(this.listeners) as Array<keyof TEventMap>
  }

  // Get listener count for an event
  listenerCount<K extends keyof TEventMap>(event: K): number {
    return this.listeners[event]?.length ?? 0
  }
}
